class FeaturedCollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.collectionSelect = this.querySelector('[data-featured-collection-select]');
    this.sortSelect = this.querySelector('[data-featured-collection-sort]');
    this.sections = [];
    this.selectedSectionId = '';

    this.handleCollectionChange = this.handleCollectionChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
  }

  connectedCallback() {
    this.discoverSections();
    this.populateCollectionOptions();
    this.applyVisibility();

    if (this.sections.length === 0) {
      this.hidden = true;
      return;
    }

    this.collectionSelect?.addEventListener('change', this.handleCollectionChange);
    this.sortSelect?.addEventListener('change', this.handleSortChange);
  }

  disconnectedCallback() {
    this.collectionSelect?.removeEventListener('change', this.handleCollectionChange);
    this.sortSelect?.removeEventListener('change', this.handleSortChange);
  }

  discoverSections() {
    this.sections = Array.from(document.querySelectorAll('[data-featured-collection-root]'))
      .map((root) => {
        const shopifySection = root.closest('.shopify-section');
        const grid = root.querySelector('[data-featured-collection-grid]');

        if (!shopifySection || !grid) return null;

        return {
          id: root.dataset.sectionId,
          label: root.dataset.collectionLabel || '',
          root,
          shopifySection,
          grid,
        };
      })
      .filter(Boolean);
  }

  populateCollectionOptions() {
    if (!this.collectionSelect) return;

    const existingOptions = this.collectionSelect.querySelectorAll('option:not([value=""])');
    existingOptions.forEach((option) => option.remove());

    this.sections.forEach((section) => {
      const option = document.createElement('option');
      option.value = section.id;
      option.textContent = section.label || section.id;
      this.collectionSelect.appendChild(option);
    });
  }

  handleCollectionChange() {
    this.selectedSectionId = this.collectionSelect?.value || '';
    this.applyVisibility();
    this.applySort();
  }

  handleSortChange() {
    this.applySort();
  }

  applyVisibility() {
    this.sections.forEach((section) => {
      const isVisible = !this.selectedSectionId || section.id === this.selectedSectionId;
      section.shopifySection.toggleAttribute('hidden', !isVisible);
    });
  }

  applySort() {
    const sortValue = this.sortSelect?.value || 'featured';
    const targets = this.getSortTargets();

    targets.forEach((section) => {
      this.sortGrid(section.grid, sortValue);
      this.refreshSlider(section.grid);
    });
  }

  getSortTargets() {
    if (this.selectedSectionId) {
      return this.sections.filter((section) => section.id === this.selectedSectionId);
    }

    return this.sections.filter((section) => !section.shopifySection.hasAttribute('hidden'));
  }

  sortGrid(grid, sortValue) {
    const items = Array.from(grid.querySelectorAll('li.grid__item[data-product-index]'));

    if (items.length === 0) return;

    if (sortValue === 'featured') {
      items
        .sort((a, b) => this.getProductIndex(a) - this.getProductIndex(b))
        .forEach((item) => grid.appendChild(item));
      return;
    }

    items.sort((a, b) => this.compareItems(a, b, sortValue)).forEach((item) => grid.appendChild(item));
  }

  compareItems(itemA, itemB, sortValue) {
    switch (sortValue) {
      case 'price-ascending':
        return this.getProductPrice(itemA) - this.getProductPrice(itemB);
      case 'price-descending':
        return this.getProductPrice(itemB) - this.getProductPrice(itemA);
      case 'title-ascending':
        return this.getProductTitle(itemA).localeCompare(this.getProductTitle(itemB));
      case 'title-descending':
        return this.getProductTitle(itemB).localeCompare(this.getProductTitle(itemA));
      case 'created-descending':
        return this.getProductDate(itemB) - this.getProductDate(itemA);
      case 'created-ascending':
        return this.getProductDate(itemA) - this.getProductDate(itemB);
      default:
        return this.getProductIndex(itemA) - this.getProductIndex(itemB);
    }
  }

  getProductIndex(item) {
    return Number.parseInt(item.dataset.productIndex, 10) || 0;
  }

  getProductPrice(item) {
    return Number.parseInt(item.dataset.productPrice, 10) || 0;
  }

  getProductTitle(item) {
    return (item.dataset.productTitle || '').toLowerCase();
  }

  getProductDate(item) {
    return Number.parseInt(item.dataset.productDate, 10) || 0;
  }

  refreshSlider(grid) {
    const slider = grid.closest('slider-component');
    if (slider && typeof slider.resetPages === 'function') {
      slider.resetPages();
      if (slider.slider) slider.slider.scrollLeft = 0;
    }
  }
}

customElements.define('featured-collection-filters', FeaturedCollectionFilters);
